import _get from 'lodash-es/get';

import {DatatableStoreState, DatatableStoreActions} from '@common/types/stores/datatableStore.types';
import {API_FILTER_SIZE, API_FILTER_AFTER} from '@common/constants/datatable/datatableConstants';
import JwtScope from '@common/constants/authentication/JwtScopeConstants';
import {Config, Filters, Model, FilterValue} from '@common/types/components/datatable.types';
import useAuthenticationStore from '@common/stores/authenticationStore';
import {apiGetDocuments} from '@common/api/apiDocuments';
import {initializePaginationFilters} from '@common/helpers/store/datatableHelper';
import {createCustomStore} from '@common/helpers/store/storeHelper';
import {Uuid} from '@common/types/index.types';
import {getEndPoints} from './configStore';

const initPaginationFiltersState = {
  [API_FILTER_AFTER]: ''
};

export const initialState: DatatableStoreState = {
  cancelSources: {},
  checkedRows: [],
  filters: {
    ...initPaginationFiltersState,
    [API_FILTER_SIZE]: 10
  },
  hasCheckedRowsChanges: false,
  isAllRowsChecked: false,
  isBarFiltersOpen: false,
  isEditOpen: false,
  isInitLoading: false,
  isLoading: false,
  isMediaEditOpen: false,
  items: [],
  models: [],
  previousItemsAfterTokens: [],
  sorts: {},
  totalItems: 0,
  totalPages: 1,
  zoomItemsUuid: []
};

export interface DatatableProps {
  name?: string; // Suffix name to identify the datatable store in the redux devtools in case of multiple datatable components on screen
}

export type DatatableStore = ReturnType<typeof createDatatableStore>;

export const createDatatableStore = (initProps?: DatatableProps) => {
  const name = initProps?.name;
  const storeName = name ? `datatable_${name}` : 'datatable';

  return createCustomStore<DatatableStoreState & DatatableStoreActions>(storeName, set => ({
    ...initialState,

    fetchData: async (filters: Filters = {}, config?: Config) => {
      const endpoints = getEndPoints();
      const {scopedJwts, stsCredentials} = useAuthenticationStore.getState();

      set(
        state => {
          state.isLoading = true;
          state.zoomItemsUuid = [];

          if (config?.initCheckedRows) {
            state.checkedRows = Array.from(config.initCheckedRows);
          }
        },
        undefined,
        'fetchData/pending'
      );

      const apiFilters = {
        ...filters,
        ...config?.initFilters
      };

      const {total, data, facets} = await apiGetDocuments(endpoints.apiDocuments, apiFilters, stsCredentials, _get(scopedJwts, [JwtScope.WEBAPP]));

      const models = Object.keys(facets || {}).reduce((acc: Model[], facetKey) => {
        acc.push({
          key: facetKey,
          items: _get(facets, [facetKey as string], []).map(({key, label, count}) => ({
            key,
            label: label || key,
            count
          }))
        });
        return acc;
      }, []);

      set(
        state => {
          if (data?.length > 0) {
            state.zoomItemsUuid = data.filter(item => item?.thumbnail).map(item => item.uuid);
          }
          state.isLoading = false;
          state.items = config?.transformAfterFetch ? config.transformAfterFetch(data) : data;
          state.totalItems = total;
          state.totalPages = Math.ceil(total / (state.filters[API_FILTER_SIZE] as number));
          state.models = models;
        },
        undefined,
        'fetchData/fulfilled'
      );
    },

    setFilters: (filters: Filters) => {
      set(
        state => {
          state.filters = filters;
        },
        undefined,
        'setFilters'
      );
    },

    setFilter: (key: string, value: FilterValue) => {
      set(
        state => {
          state.filters = initializePaginationFilters(
            {
              ...state.filters,
              [key]: value
            },
            initPaginationFiltersState,
            key
          );
        },
        undefined,
        'setFilter'
      );
    },

    removeFilter: (key: string) => {
      set(
        state => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const {[key]: _keyToRemove, ...stateFilters} = state.filters;
          state.filters = initializePaginationFilters(stateFilters, initPaginationFiltersState, key);
        },
        undefined,
        'removeFilter'
      );
    },

    setCheckedRows: (checkedRows: Set<Uuid>) => {
      set(
        state => {
          state.checkedRows = Array.from(checkedRows);
        },
        undefined,
        'setCheckedRows'
      );
    },

    toggleRowSelection: (key: Uuid) => {
      set(
        state => {
          const checkedRows = new Set(state.checkedRows);
          if (checkedRows.has(key)) {
            checkedRows.delete(key);
          } else {
            checkedRows.add(key);
          }
          state.checkedRows = Array.from(checkedRows);
          state.hasCheckedRowsChanges = true;
        },
        undefined,
        'toggleRowSelection'
      );
    },

    toggleSinglyRowSelection: (key: Uuid) => {
      set(
        state => {
          const checkedRows = new Set(state.checkedRows);
          state.checkedRows = checkedRows.has(key) ? [] : [key];
        },
        undefined,
        'toggleSinglyRowSelection'
      );
    },

    init: (config: Config) => {
      set(
        state => {
          state.isInitLoading = true;
        },
        undefined,
        'init/startLoading'
      );

      set(
        state => {
          if (config.defaultFilters) {
            state.filters = {
              ...config.defaultFilters,
              ...state.filters
            };
          }
          if (config.initCheckedRows) {
            state.checkedRows = Array.from(config.initCheckedRows);
          }
        },
        undefined,
        'init/setValues'
      );

      set(
        state => {
          state.isInitLoading = false;
        },
        undefined,
        'init/endLoading'
      );
    },

    reset: () => {
      set(() => initialState, undefined, 'reset');
    },

    setPreviousItemsAfterTokens: (previousItems: string[]) => {
      set(
        state => {
          state.previousItemsAfterTokens = previousItems;
        },
        undefined,
        'setPreviousItemsAfterTokens'
      );
    },

    toggleFiltersBarVisibility: (visibility?: boolean) => {
      set(
        state => {
          state.isBarFiltersOpen = visibility ?? !state.isBarFiltersOpen;
        },
        undefined,
        'toggleFiltersBarVisibility'
      );
    },

    setIsEditOpen: (isEditOpen: boolean) => {
      set(
        state => {
          state.isEditOpen = isEditOpen;
        },
        undefined,
        'setIsEditOpen'
      );
    },

    setIsMediaEditOpen: (isMediaEditOpen: boolean) => {
      set(
        state => {
          state.isMediaEditOpen = isMediaEditOpen;
        },
        undefined,
        'openDetailedView/setIsMediaEditOpen'
      );
    },

    isCheckedRowsChanged: () => {
      return false;
    }
  }));
};
